/*
 * WOEST CUSTOM (okt 2026) – productpagina. Zie WOEST-CUSTOM.md.
 *
 * De opbouw (tabs, één specificatietabel, intro, pop-up met het vraagformulier) komt kant-en-klaar
 * van de server (snippets/woest-tabs.liquid, woest-specs.liquid, product-block.liquid). Dit script
 * verplaatst niets op de pagina; het regelt alleen het gedrag:
 *
 * 1. <woest-sticky-atc>: meescrollende balk met gekozen opties, prijs en knop (verschijnt na de echte knop).
 * 2. Tabs: tab wisselen (desktop), uitklappen en "Lees meer" (mobiel), tab openen bij een blok in de editor.
 * 3. Links naar #product-contact openen de pop-up met het vraagformulier (en sturen de gekozen variant mee).
 */
(function () {
  'use strict';

  // Eén keer per pagina: het thema kan dit script opnieuw uitvoeren (thema-editor, quick-add)
  if (window.__woestProduct) return;
  window.__woestProduct = true;

  /* ---------- 1. Meescrollende balk ---------- */

  if (!customElements.get('woest-sticky-atc')) {
    class WoestStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.section = document.getElementById('shopify-section-' + this.sectionId);
        const template = this.querySelector('template');

        if (!this.section || !template || !this.section.contains(this)) return;
        if (this.closest('quick-add-modal, modal-dialog, [role="dialog"]')) return;

        const visibility = this.dataset.visibility || '';
        this.mq = window.matchMedia(
          visibility === 'visible-xs' ? '(max-width: 989.98px)' : visibility === 'hidden-xs' ? '(min-width: 990px)' : 'all'
        );

        document.getElementById('WoestStickyAtc')?.remove();
        this.bar = template.content.firstElementChild.cloneNode(true);
        document.body.appendChild(this.bar);

        this.priceEl = this.bar.querySelector('[data-wsa-price]');
        this.compareEl = this.bar.querySelector('[data-wsa-compare]');
        this.labelEl = this.bar.querySelector('[data-wsa-label]');
        this.variantEl = this.bar.querySelector('[data-wsa-variant]');

        this.onClick = this.onClick.bind(this);
        this.update = this.update.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.bar.querySelector('[data-wsa-button]').addEventListener('click', this.onClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.mq.addEventListener?.('change', this.update);

        this.onChange = () => this.scheduleSync();
        this.section.addEventListener('change', this.onChange);

        // Alleen reageren op wijzigingen in prijs, winkelwagenformulier en variantkeuze (niet op de galerij e.d.)
        this.observer = new MutationObserver((mutations) => {
          if (mutations.some((mutation) => this.isRelevant(mutation.target))) this.scheduleSync();
        });
        this.observer.observe(this.section, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'class'],
        });

        this.sync();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.io?.disconnect();
        this.section?.removeEventListener('change', this.onChange);
        clearTimeout(this.timer);
        window.removeEventListener('scroll', this.onScroll);
        this.mq?.removeEventListener?.('change', this.update);
        this.bar?.remove();
        // Bij een kleurwissel staat de nieuwe balk er soms al; die beheert de klasse dan zelf
        if (!document.getElementById('WoestStickyAtc')) document.body.classList.remove('woest-sticky-atc-on');
      }

      isRelevant(node) {
        const element = node && node.nodeType === 1 ? node : node && node.parentElement;
        return !!(
          element &&
          element.closest('[id^="price-"], product-form, variant-selects, [id^="ProductSubmitButton-"]')
        );
      }

      getMainButton() {
        return document.getElementById('ProductSubmitButton-' + this.sectionId);
      }

      // Alleen tonen als de bezoeker voorbij de echte knop is gescrold (knop boven de rand van het scherm).
      // Staat de knop nog onder in beeld, dan blijft de balk weg: dan zou hij de maat- en kleurkeuze afdekken.
      isButtonPassed() {
        if (!this.watchedButton) return false;
        // Een meescrollende kop bedekt de bovenkant van het scherm: de knop is dan al weg als hij onder de kop zit
        const header = document.querySelector('.section-header');
        const offset = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        return this.watchedButton.getBoundingClientRect().bottom <= offset;
      }

      onScroll() {
        const passed = this.isButtonPassed();
        if (passed !== this.buttonPassed) {
          this.buttonPassed = passed;
          this.update();
        }
      }

      // Gekozen opties die de bezoeker echt kan wisselen (bijv. "28mm"), zodat zichtbaar is wat er in de winkelwagen gaat
      getVariantSummary() {
        const selects = this.section.querySelector('variant-selects');
        if (!selects) return '';
        // EU/US-maatwissel bij schoenen: toon de stand die de bezoeker ziet
        const hiddenUnit = document.documentElement.getAttribute('data-woest-size') === 'us' ? 'eu' : 'us';
        const labelText = (fieldset, input) => {
          const label = input.id ? fieldset.querySelector('label[for="' + CSS.escape(input.id) + '"]') : null;
          if (!label) return input.value.trim();
          const clone = label.cloneNode(true);
          clone.querySelectorAll('.visually-hidden, .label-unavailable, .woest-size-' + hiddenUnit).forEach((n) => n.remove());
          return clone.textContent.replace(/\s+/g, ' ').trim() || input.value.trim();
        };
        const parts = [];
        selects.querySelectorAll('fieldset').forEach((fieldset) => {
          if (fieldset.querySelectorAll('input[type="radio"]').length < 2) return;
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked && checked.value) parts.push(labelText(fieldset, checked));
        });
        selects.querySelectorAll('select').forEach((select) => {
          if (select.options.length < 2 || !select.value) return;
          parts.push(select.value.trim());
        });
        return parts.join(' · ');
      }

      scheduleSync() {
        if (this.timer) return;
        this.timer = setTimeout(() => {
          this.timer = null;
          this.sync();
        }, 50);
      }

      sync() {
        if (!this.bar) return;
        const button = this.getMainButton();

        // De echte knop kan na een variantwissel vervangen zijn: opnieuw volgen
        if (button !== this.watchedButton) {
          this.io?.disconnect();
          this.watchedButton = button;
          this.buttonPassed = this.isButtonPassed();
          if (button) {
            this.io = new IntersectionObserver(() => {
              this.buttonPassed = this.isButtonPassed();
              this.update();
            });
            this.io.observe(button);
          }
        }

        const wrapper = document.getElementById('price-' + this.sectionId);
        if (wrapper) {
          const price = wrapper.querySelector('.price');
          const onSale = !!price && price.classList.contains('price--on-sale');
          const current = onSale
            ? wrapper.querySelector('.price__sale .price-item--sale')
            : wrapper.querySelector('.price__regular .price-item--regular');
          const compare = onSale ? wrapper.querySelector('.price__sale s') : null;
          this.priceEl.textContent = current ? current.textContent.trim() : '';
          this.priceEl.style.color = current && onSale ? getComputedStyle(current).color : '';
          this.compareEl.textContent = compare ? compare.textContent.trim() : '';
          this.compareEl.hidden = !compare || !compare.textContent.trim();
        }

        if (this.variantEl) {
          const summary = this.getVariantSummary();
          this.variantEl.textContent = summary;
          this.variantEl.hidden = !summary;
        }

        if (button) {
          const disabled = button.disabled || button.getAttribute('aria-disabled') === 'true';
          const mainLabel = (button.querySelector('span') || button).textContent.trim();
          this.labelEl.textContent = disabled ? mainLabel : this.dataset.addLabel || mainLabel;
          this.bar.classList.toggle('woest-sticky-atc--disabled', disabled);
          this.bar.classList.toggle('woest-sticky-atc--loading', button.classList.contains('loading'));
        }

        this.update();
      }

      update() {
        if (!this.bar) return;
        const show = this.mq.matches && !!this.watchedButton && this.buttonPassed === true;
        this.bar.classList.toggle('is-visible', show);
        document.body.classList.toggle('woest-sticky-atc-on', show);
        if (show) {
          const height = this.bar.offsetHeight;
          if (height && height !== this.lastHeight) {
            this.lastHeight = height;
            document.documentElement.style.setProperty('--woest-sticky-atc-h', height + 'px');
          }
        }
      }

      onClick() {
        const button = this.getMainButton();
        if (!button) return;

        if (button.disabled || button.getAttribute('aria-disabled') === 'true') {
          const options =
            this.section.querySelector('variant-selects') || this.section.querySelector('.product-form__input') || button;
          options.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        button.click();
      }
    }

    customElements.define('woest-sticky-atc', WoestStickyAtc);
  }

  /* ---------- 2. Tabs (opbouw komt van de server) ---------- */

  // requestAnimationFrame, met een terugval voor tabbladen op de achtergrond (daar draait rAF niet)
  function nextFrame(callback) {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      callback();
    };
    requestAnimationFrame(run);
    setTimeout(run, 100);
  }

  function setOpen(item, open) {
    item.classList.toggle('is-open', open);
    const toggle = item.querySelector(':scope > .woest-tabs__toggle');
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    const more = item.querySelector(':scope > .woest-tabs__more');
    if (more) more.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function activateTab(tabs, item, focus) {
    tabs.querySelectorAll(':scope > .woest-tabs__item').forEach((other) => {
      const active = other === item;
      other.classList.toggle('is-active', active);
      const panel = other.querySelector(':scope > .woest-tabs__panel');
      const tab = panel && tabs.querySelector('#' + CSS.escape(panel.getAttribute('aria-labelledby') || ''));
      if (tab) {
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      }
    });
  }

  // Zoek binnen dezelfde tabgroep (een snelle-toevoegen-venster kan dezelfde tabs nog een keer bevatten)
  function itemForTab(tab) {
    const tabs = tab.closest('.woest-tabs');
    const id = tab.getAttribute('aria-controls');
    const panel = tabs && id && tabs.querySelector('#' + CSS.escape(id));
    return panel && panel.closest('.woest-tabs__item');
  }

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('.woest-tabs__tab');
    if (tab) {
      const item = itemForTab(tab);
      if (item) activateTab(tab.closest('.woest-tabs'), item);
      return;
    }

    const toggle = event.target.closest('.woest-tabs__toggle');
    if (toggle) {
      const item = toggle.closest('.woest-tabs__item');
      setOpen(item, !item.classList.contains('is-open'));
      return;
    }

    const more = event.target.closest('.woest-tabs__more');
    if (more) {
      const item = more.closest('.woest-tabs__item');
      setOpen(item, true);
      // De knop verdwijnt; focus naar de kop zodat toetsenbordgebruikers niet bovenaan de pagina belanden
      item.querySelector(':scope > .woest-tabs__toggle')?.focus({ preventScroll: true });
    }
  });

  // Pijltjestoetsen, Home en End tussen de tabknoppen
  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest && event.target.closest('.woest-tabs__tab');
    if (!tab) return;
    const tabs = [...tab.parentElement.querySelectorAll('.woest-tabs__tab')];
    const index = tabs.indexOf(tab);
    let nextIndex = -1;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex < 0) return;
    event.preventDefault();
    const next = tabs[nextIndex];
    const item = itemForTab(next);
    if (item) activateTab(next.closest('.woest-tabs'), item, true);
  });

  // Mobiel: korte teksten helemaal tonen (zonder vervaging en "Lees meer"); meten in het volgende frame
  function markShortIntros(root) {
    if (!window.matchMedia('(max-width: 749.98px)').matches) return;
    const items = [...root.querySelectorAll('.woest-tabs__item.has-intro:not(.is-short):not(.is-open)')];
    if (!items.length) return;
    nextFrame(() => {
      items.forEach((item) => {
        const panel = item.querySelector(':scope > .woest-tabs__panel');
        if (panel && panel.scrollHeight <= panel.clientHeight + 8) item.classList.add('is-short');
      });
    });
  }

  /* ---------- 3. Vraagformulier in een pop-up (pop-up komt van de server) ---------- */

  function enquiryModalFor(element) {
    const section = element.closest('[id^="shopify-section-"]');
    const id = section ? section.id.replace('shopify-section-', '') : '';
    // Het thema verplaatst de pop-up bij het openen naar <body>; zoek daarom op data-section-id.
    // Geen terugval op "de eerste pop-up op de pagina": in een snelle-toevoegen-venster zou dat
    // het formulier van een ander product openen. Zonder eigen pop-up gaat de link naar de contactpagina.
    return id ? document.querySelector('.woest-enquiry-modal[data-section-id="' + CSS.escape(id) + '"]') : null;
  }

  function openEnquiry(modal, opener) {
    // Direct onder <body> zetten, zodat geen enkele omliggende opmaak de pop-up kan beïnvloeden
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
    const section = document.getElementById('shopify-section-' + modal.dataset.sectionId);
    const selected = section && section.querySelector('product-form form input[name="id"]');
    const field = modal.querySelector('input[name="contact[Variant ID]"]');
    if (selected && field && selected.value) field.value = selected.value;
    if (typeof modal.show === 'function') modal.show(opener, 'popup-modal--pdp');
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href="#product-contact"], a[href="#product-enquiry"]');
    if (!link) return;
    const modal = enquiryModalFor(link);
    event.preventDefault();
    if (modal && typeof modal.show === 'function') {
      openEnquiry(modal, link);
    } else {
      // Geen formulier op deze pagina: naar de contactpagina (in de juiste taal)
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = rootUrl + 'pages/contact';
    }
  });

  // Variant meesturen bij verzenden (ook als de bezoeker na het openen nog een andere maat koos)
  document.addEventListener(
    'submit',
    (event) => {
      const modal = event.target.closest && event.target.closest('.woest-enquiry-modal');
      if (!modal) return;
      const section = document.getElementById('shopify-section-' + modal.dataset.sectionId);
      const selected = section && section.querySelector('product-form form input[name="id"]');
      const field = modal.querySelector('input[name="contact[Variant ID]"]');
      if (selected && field && selected.value) field.value = selected.value;
      // Onthouden dat dit formulier is verstuurd (de Back in Stock-melding gebruikt dezelfde terugkeer-URL)
      try {
        sessionStorage.setItem('woest-enquiry-sent', '1');
      } catch (e) {}
    },
    true
  );

  function prepareEnquiryLinks(root) {
    root.querySelectorAll('a[href="#product-contact"], a[href="#product-enquiry"]').forEach((link) => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.removeAttribute('title');
    });
  }

  // Na verzenden laadt de pagina opnieuw: toon de melding (bedankt of fout) meteen in de pop-up
  function reopenAfterSubmit() {
    const modal = document.querySelector('.woest-enquiry-modal');
    if (!modal) return;
    const posted = /[?&]contact_posted=true/.test(window.location.search) || modal.querySelector('.form__message, .form-status');
    if (!posted) return;
    // Alleen openen als juist dit formulier is verstuurd, niet na een Back in Stock-aanmelding
    let sentHere = true;
    try {
      sentHere = sessionStorage.getItem('woest-enquiry-sent') === '1';
      sessionStorage.removeItem('woest-enquiry-sent');
    } catch (e) {}
    if (!sentHere) return;
    customElements.whenDefined('modal-dialog').then(() => setTimeout(() => openEnquiry(modal, null), 300));
  }

  // Na een kleurwissel of herladen in de editor staat er een verse pop-up in de sectie;
  // eerder geopende (naar <body> verplaatste) pop-ups horen bij het vorige product
  function removeStaleModals() {
    document.querySelectorAll('body > .woest-enquiry-modal').forEach((modal) => modal.remove());
  }

  /* ---------- Opstarten ---------- */

  function init(root) {
    markShortIntros(root);
    prepareEnquiryLinks(root);
  }

  const start = () => {
    init(document);
    reopenAfterSubmit();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Kleurwissel (swatches van gekoppelde producten) vervangt de productsectie zonder de pagina te herladen
  document.addEventListener('ws:product-swapped', () => {
    removeStaleModals();
    init(document);
  });

  // Thema-editor: na herladen opnieuw voorbereiden en de tab van het geselecteerde blok openen
  document.addEventListener('shopify:section:load', (event) => {
    removeStaleModals();
    init(event.target);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest('.woest-tabs__item');
    if (!item) return;
    activateTab(item.closest('.woest-tabs'), item);
    setOpen(item, true);
  });
})();
